import React, { useEffect, useMemo, useState } from 'react';
import {
  TextField,
  Typography,
  Box,
  Alert,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { Storefront as VendorIcon } from '@mui/icons-material';
import type { Vendor } from '@stash/shared-types';
import VendorEditForm from './VendorEditForm';
import DataService from '../../services/data-service';
import { useRoleCheck } from '../../hooks/use-role-check';
import EditPageLayout from '../common/EditPageLayout';
import { AdminVendorsStoreProvider } from '../../store/admin-vendors/context';
import {
  useAdminVendors,
  useAdminVendorsLoading,
  useAdminVendorsError,
  useLoadAllVendors,
  useReloadVendors,
} from '../../store/admin-vendors/hooks';
import {
  searchVendors,
  buildVendorFuse,
  composeAddress,
  displayCompanyName,
  highlightMatch,
} from './vendorSearch';

const VendorEditPageContent = (): React.JSX.Element => {
  const { isAdmin, isLoading: roleLoading, error: roleError } = useRoleCheck();

  const vendors = useAdminVendors();
  const listLoading = useAdminVendorsLoading();
  const listError = useAdminVendorsError();
  const loadAll = useLoadAllVendors();
  const reload = useReloadVendors();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectLoading, setSelectLoading] = useState(false);
  const [selectError, setSelectError] = useState<string | null>(null);

  const isUserAdmin = !roleLoading && isAdmin();

  // Load the full catalog once when an admin opens the page.
  useEffect(() => {
    if (isUserAdmin) void loadAll();
  }, [isUserAdmin, loadAll]);

  // Memoize the Fuse index against the loaded catalog; filter in-memory per keystroke.
  const fuse = useMemo(() => buildVendorFuse(vendors), [vendors]);
  const results = useMemo(() => searchVendors(vendors, query, { fuse }), [vendors, query, fuse]);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setQuery(event.target.value);
    setActiveIndex(-1);
    // Typing returns to search mode (clears any selected/edited vendor + error).
    setSelectedVendor(null);
    setSelectError(null);
  };

  // The in-memory list is for find-and-display only; always re-fetch the
  // authoritative vendor before editing so edits never apply to stale data.
  const handleSelect = async (recordID: string): Promise<void> => {
    setSelectLoading(true);
    setSelectError(null);
    setSelectedVendor(null);
    try {
      const fresh = (await new DataService().getVendor(recordID)) as Vendor | null;
      if (fresh) {
        setSelectedVendor(fresh);
      } else {
        setSelectError(`Vendor with Record ID "${recordID}" not found`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSelectError(`Failed to load vendor: ${message}`);
    } finally {
      setSelectLoading(false);
    }
  };

  const handleVendorUpdate = (updatedVendor: unknown): void => {
    setSelectedVendor(updatedVendor as Vendor);
  };

  // Combobox keyboard navigation over the results list.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      setQuery('');
      setActiveIndex(-1);
      return;
    }
    if (!results.length) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        if (activeIndex >= 0 && results[activeIndex]) {
          event.preventDefault();
          void handleSelect(results[activeIndex].recordID);
        }
        break;
      default:
        break;
    }
  };

  const getPageError = (): string | null => {
    if (roleError) return 'Failed to verify your permissions. Please try refreshing the page.';
    if (!roleLoading && !isAdmin())
      return 'You must have admin privileges to access the vendor edit feature.';
    return null;
  };

  const renderHighlighted = (text: string): React.ReactNode =>
    highlightMatch(text, query).map((segment, i) =>
      segment.match ? (
        <strong key={i}>{segment.text}</strong>
      ) : (
        <React.Fragment key={i}>{segment.text}</React.Fragment>
      ),
    );

  const showResults = Boolean(query.trim()) && !selectedVendor && !selectLoading && !listLoading;

  return (
    <EditPageLayout
      title="Vendor Edit"
      titleIcon={<VendorIcon />}
      subtitle="Search for a vendor by company or contact name to edit a record."
      isLoading={roleLoading}
      error={getPageError()}
    >
      {isUserAdmin && (
        <>
          <TextField
            fullWidth
            label="Search vendors"
            variant="outlined"
            value={query}
            onChange={handleQueryChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a company or contact name, or paste a record ID…"
            disabled={listLoading}
            helperText={
              listLoading ? 'Loading vendors…' : `${vendors.length} vendors loaded`
            }
            sx={{ marginBottom: 2 }}
            inputProps={{
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': showResults && results.length > 0,
              'aria-controls': 'vendor-results-listbox',
              'aria-activedescendant':
                activeIndex >= 0 && results[activeIndex]
                  ? `vendor-option-${results[activeIndex].recordID}`
                  : undefined,
            }}
          />

          {listLoading && (
            <Box
              role="status"
              aria-live="polite"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}
            >
              <CircularProgress size={20} />
              <Typography>Loading vendors…</Typography>
            </Box>
          )}

          {listError && !listLoading && (
            <Alert
              severity="error"
              sx={{ marginBottom: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => void reload()}>
                  Retry
                </Button>
              }
            >
              Failed to load vendors: {listError}
            </Alert>
          )}

          {selectError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {selectError}
            </Alert>
          )}

          {showResults &&
            (results.length > 0 ? (
              <List
                id="vendor-results-listbox"
                role="listbox"
                aria-label="Vendor search results"
                data-testid="vendor-results"
                dense
              >
                {results.map((vendor, index) => (
                  <ListItemButton
                    key={vendor.recordID}
                    id={`vendor-option-${vendor.recordID}`}
                    role="option"
                    aria-selected={index === activeIndex}
                    selected={index === activeIndex}
                    data-testid={`vendor-result-${vendor.recordID}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => void handleSelect(vendor.recordID)}
                  >
                    <ListItemText
                      primary={renderHighlighted(displayCompanyName(vendor))}
                      secondary={[vendor.contactName, composeAddress(vendor.address)]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography role="status" sx={{ marginY: 2 }}>
                No vendors found
              </Typography>
            ))}

          {selectLoading && (
            <Box
              role="status"
              aria-live="polite"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}
            >
              <CircularProgress size={20} />
              <Typography>Loading vendor…</Typography>
            </Box>
          )}

          {selectedVendor && !selectLoading && (
            <Box sx={{ marginTop: 2, '@media (min-width: 960px)': { marginTop: 3 } }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: '1.25rem',
                  '@media (min-width: 960px)': { fontSize: '1.5rem' },
                }}
              >
                Editing: {displayCompanyName(selectedVendor)}
              </Typography>
              <VendorEditForm
                vendor={
                  selectedVendor as unknown as React.ComponentProps<typeof VendorEditForm>['vendor']
                }
                onVendorUpdate={handleVendorUpdate}
              />
            </Box>
          )}
        </>
      )}
    </EditPageLayout>
  );
};

const VendorEditPage = (): React.JSX.Element => (
  <AdminVendorsStoreProvider>
    <VendorEditPageContent />
  </AdminVendorsStoreProvider>
);

export default VendorEditPage;
